import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.jsx';
import { useToast, Field } from '../components/ui.jsx';
import Icon from '../components/Icon.jsx';
import AuthShell, { AuthHead, TrustStrip } from '../components/AuthShell.jsx';
import { firstName } from '../lib/format.js';

const DEMO = [
  { label: 'Patient', email: 'anjali@example.com', icon: 'user' },
  { label: 'Nurse', email: 'lakshmi@nurse.in', icon: 'stethoscope' },
  { label: 'Admin', email: 'admin@healathome.in', icon: 'shield' },
];

export default function Login() {
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e, creds) => {
    e?.preventDefault();
    setBusy(true);
    setError('');
    try {
      const user = await login(creds?.email ?? email, creds?.password ?? password);
      toast.success(`Welcome back, ${firstName(user.name)}`);
      const dest = location.state?.from || (user.role === 'nurse' ? '/nurse' : user.role === 'admin' ? '/admin' : '/');
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell below={<TrustStrip />}>
      {/* `--i` staggers each block's entrance, top to bottom. */}
      <div style={{ '--i': 0 }}>
        <AuthHead title="Welcome back" subtitle="Sign in to manage your visits and bookings." />
      </div>

      {error && (
        <div className="banner banner-danger" style={{ '--i': 1 }} role="alert">
          <Icon name="warning" />
          <span className="grow">{error}</span>
        </div>
      )}

      <form onSubmit={submit} style={{ '--i': 1 }}>
        <Field label="Email address" id="email">
          <div className="input-icon">
            <Icon name="mail" />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              autoFocus
              required
            />
          </div>
        </Field>

        <Field label="Password" id="password">
          <div className="input-icon">
            <Icon name="lock" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              style={{ paddingRight: 46 }}
              required
            />
            <button
              type="button"
              className="input-clear"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              <Icon name="eye" />
            </button>
          </div>
          {/* Outside the <label> so the link is not nested interactive content. */}
          <div className="field-assist">
            <Link to="/forgot-password">Forgot password?</Link>
          </div>
        </Field>

        <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? (
            <span className="spinner" />
          ) : (
            <>
              Sign in <Icon name="arrow-right" />
            </>
          )}
        </button>
      </form>

      <div className="auth-divider" style={{ '--i': 2 }}>
        <span>or try a demo account</span>
      </div>

      <div className="demo-row" style={{ '--i': 3 }}>
        {DEMO.map((d) => (
          <button
            key={d.email}
            type="button"
            className="demo-btn"
            disabled={busy}
            title={`Sign in as ${d.email}`}
            onClick={(e) => submit(e, { email: d.email, password: 'password123' })}
          >
            <span className="demo-avatar">
              <Icon name={d.icon} />
            </span>
            {d.label}
          </button>
        ))}
      </div>

      <p className="tiny dim center mt-2" style={{ '--i': 4 }}>
        Every demo account uses the password <code>password123</code>
      </p>

      <p className="auth-foot" style={{ '--i': 5 }}>
        New to HealAtHome? <Link to="/register">Create an account</Link>
      </p>
    </AuthShell>
  );
}
